import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Lock, LogOut, Plus, RefreshCcw, Save, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const STORAGE_KEY = 'admin_services_password';
const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
];

const PAGE_EDITORS = [
  { key: 'homePage', label: 'Home Page', title: 'Home Page', description: 'Edit the headline, image, section titles, and call to action on the home page.', fields: [
    ['homeHeroImage', 'Hero Image URL', 'image'], ['homeBadge', 'Hero Badge'],
    ['homeHeroTitle', 'Hero Title'], ['homeHeroSubtitle', 'Hero Subtitle', 'textarea'],
    ['homeHeroDescription', 'Hero Description', 'textarea'], ['homeHeroButton', 'Hero Booking Button'],
    ['homeExpertiseTitle', 'Expertise Section Title'], ['homeExpertiseSubtitle', 'Expertise Section Subtitle', 'textarea'],
    ['homeServicesTitle', 'Services Section Title'], ['homeServicesSubtitle', 'Services Section Subtitle', 'textarea'],
    ['homeExperiencesTitle', 'Client Experiences Title'], ['homeExperiencesSubtitle', 'Client Experiences Subtitle', 'textarea'],
    ['homeCtaTitle', 'Final CTA Title'], ['homeCtaSubtitle', 'Final CTA Subtitle', 'textarea'],
    ['homeContactButton', 'Final CTA Contact Button'],
  ]},
  { key: 'contactPage', label: 'Contact Page', title: 'Contact Page', description: 'Edit the main heading and contact form title.', fields: [
    ['contactHeroTitle', 'Hero Title'], ['contactHeroSubtitle', 'Hero Subtitle', 'textarea'],
    ['contactFormTitle', 'Contact Form Title'],
  ]},
  { key: 'bookingPage', label: 'Booking Page', title: 'Booking Page', description: 'Edit the main booking heading and introduction.', fields: [
    ['bookingHeroTitle', 'Hero Title'], ['bookingHeroSubtitle', 'Hero Subtitle', 'textarea'],
    ['bookingFormTitle', 'Booking Form Title'], ['bookingFormDescription', 'Booking Form Description', 'textarea'],
  ]},
  { key: 'aboutLabels', label: 'About Page Labels', title: 'About Page Labels', description: 'Edit the fixed headings used on the About page.', fields: [
    ['aboutApproachTitle', 'Approach Title'], ['aboutLanguagesTitle', 'Languages Title'],
    ['aboutServiceAreasTitle', 'Service Areas Title'], ['aboutQualificationsTitle', 'Qualifications Title'],
    ['aboutCommitmentTitle', 'Commitment Title'], ['aboutCoreValuesTitle', 'Core Values Title'],
  ]},
  { key: 'servicesLabels', label: 'Services Page Labels', title: 'Services Page Labels', description: 'Edit service-detail headings and the service comparison table labels.', fields: [
    ['serviceDetailNotFound', 'Service Not Found Message'], ['serviceDetailOverview', 'Service Detail Overview Title'],
    ['serviceDetailPricing', 'Service Detail Pricing Title'], ['comparisonFeature', 'Comparison Feature Column'],
    ['comparisonCoaching', 'Comparison: Coaching Column'], ['comparisonMassage', 'Comparison: Massage Column'],
    ['comparisonWorkshops', 'Comparison: Workshops Column'], ['comparisonOnlineAvailable', 'Comparison: Online Available Row'],
    ['comparisonInPerson', 'Comparison: In-Person Row'], ['comparisonSessionLength', 'Comparison: Session Length Row'],
    ['comparisonGroupSessions', 'Comparison Group Sessions Row'],
  ]},
];

const DEFAULT_SERVICES_PAGE = {
  heroTitle: {
    en: 'Professional Wellness Services',
    de: 'Professionelle Wellness-Leistungen',
    fr: 'Services de bien-être professionnels',
    es: 'Servicios profesionales de bienestar',
  },
  heroSubtitle: {
    en: 'Comprehensive mental health coaching and therapeutic massage services designed to support your unique journey towards wellbeing',
    de: 'Umfassendes Mental-Health-Coaching und therapeutische Massage zur Unterstützung Ihres individuellen Weges.',
    fr: 'Coaching en santé mentale et massage thérapeutique pour soutenir votre parcours personnel.',
    es: 'Coaching de salud mental y masaje terapéutico para apoyar tu proceso personal.',
  },
  comparisonTitle: {
    en: 'Service Comparison',
    de: 'Leistungsvergleich',
    fr: 'Comparaison des services',
    es: 'Comparación de servicios',
  },
  faqTitle: {
    en: 'Frequently Asked Questions',
    de: 'Häufige Fragen',
    fr: 'Questions fréquentes',
    es: 'Preguntas frecuentes',
  },
  ctaTitle: {
    en: 'Ready to Get Started?',
    de: 'Bereit loszulegen?',
    fr: 'Prêt(e) à vous lancer ?',
    es: '¿Listo/a para empezar?',
  },
  ctaSubtitle: {
    en: 'Take the first step towards better mental and physical wellbeing. Book your session today.',
    de: 'Machen Sie den ersten Schritt zu besserem mentalem und körperlichem Wohlbefinden.',
    fr: 'Faites le premier pas vers un meilleur équilibre mental et physique.',
    es: 'Da el primer paso hacia un mejor bienestar mental y físico.',
  },
  ctaButton: {
    en: 'Book Your Session',
    de: 'Sitzung buchen',
    fr: 'Réserver votre séance',
    es: 'Reservar tu sesión',
  },
};

const parseLines = (value) =>
  value
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);

const toLines = (items = []) => items.join('\n');

const createEmptyLocalizedText = () => ({
  en: '',
  de: '',
  fr: '',
  es: '',
});

const cloneLocalizedText = (value = {}) =>
  SUPPORTED_LANGUAGES.reduce((accumulator, language) => {
    accumulator[language.code] = String(value?.[language.code] || '').trim();
    return accumulator;
  }, {});

const cloneServicesPage = (value = DEFAULT_SERVICES_PAGE) => ({
  heroTitle: cloneLocalizedText(value.heroTitle),
  heroSubtitle: cloneLocalizedText(value.heroSubtitle),
  comparisonTitle: cloneLocalizedText(value.comparisonTitle),
  faqTitle: cloneLocalizedText(value.faqTitle),
  ctaTitle: cloneLocalizedText(value.ctaTitle),
  ctaSubtitle: cloneLocalizedText(value.ctaSubtitle),
  ctaButton: cloneLocalizedText(value.ctaButton),
});

const createDefaultContent = () => ({
  about: {
    title: '',
    subtitle: '',
    image: '',
    bio: '',
    approachParagraph: '',
    credentials: [],
    languages: [],
    serviceAreaLine1: '',
    serviceAreaLine2: '',
    commitmentText: '',
    philosophyTitle: '',
    philosophyParagraph1: '',
    philosophyParagraph2: '',
  },
  servicesPage: cloneServicesPage(),
  publicPageCopy: {},
  specialties: [],
  testimonials: [],
  coreValues: [],
  faqs: [],
  resources: [],
});

const normalizeCurrency = (value, fallback = 'EUR') => {
  const cleaned = String(value || '')
    .replace(/[^A-Za-z]/g, '')
    .toUpperCase()
    .slice(0, 3);

  return cleaned || fallback;
};

const normalizeNonNegativeNumber = (value, fallback) => {
  const parsed = Number(value);
  if (Number.isNaN(parsed) || parsed < 0) {
    return fallback;
  }
  return Math.round(parsed * 100) / 100;
};

const DEFAULT_PAYMENT_SETTINGS = {
  enableManualPayments: true,
  manualPaymentInstructions:
    'Manual payment is available after booking confirmation (for example bank transfer or cash).',
  enablePaypalPayments: false,
  paypalClientId: '',
  paypalEnvironment: 'sandbox',
  paypalCurrency: 'EUR',
  onlinePaymentTaxPercent: 19,
  onlinePaymentBookingFee: 2.5,
  onlinePaymentMinimumPercent: 50,
  cancellationWindowHours: 24,
  cancellationTerms:
    'A minimum booking payment of 50% is required when booking online, or you may pay the full amount. If a booking is cancelled within the cancellation window, no refund is provided.',
  bookingBufferMinutes: 60,
};

const normalizePaymentSettings = (payload = {}) => ({
  enableManualPayments: Boolean(payload.enableManualPayments),
  manualPaymentInstructions: String(payload.manualPaymentInstructions || '').trim(),
  enablePaypalPayments: Boolean(payload.enablePaypalPayments),
  paypalClientId: String(payload.paypalClientId || '').trim(),
  paypalEnvironment:
    String(payload.paypalEnvironment || 'sandbox').trim().toLowerCase() === 'live'
      ? 'live'
      : 'sandbox',
  paypalCurrency: normalizeCurrency(payload.paypalCurrency, 'EUR'),
  onlinePaymentTaxPercent: normalizeNonNegativeNumber(payload.onlinePaymentTaxPercent, 19),
  onlinePaymentBookingFee: normalizeNonNegativeNumber(payload.onlinePaymentBookingFee, 2.5),
  onlinePaymentMinimumPercent: Math.min(
    100,
    Math.max(50, normalizeNonNegativeNumber(payload.onlinePaymentMinimumPercent, 50)),
  ),
  cancellationWindowHours: Math.max(
    1,
    Math.round(normalizeNonNegativeNumber(payload.cancellationWindowHours, 24)),
  ),
  cancellationTerms: String(payload.cancellationTerms || '').trim(),
  bookingBufferMinutes: Math.max(
    0,
    Math.round(normalizeNonNegativeNumber(payload.bookingBufferMinutes, 60)),
  ),
});

const createEmptySpecialty = () => ({ title: '', description: '' });
const createEmptyTestimonial = (id) => ({ id, name: '', feedback: '', rating: 5 });
const createEmptyCoreValue = () => ({ title: '', description: '' });
const createEmptyFaq = () => ({ question: '', answer: '' });
const createEmptyResource = () => ({ title: '', description: '', url: '' });

const normalizeAbout = (about = {}) => ({
  title: String(about.title || '').trim(),
  subtitle: String(about.subtitle || '').trim(),
  image: String(about.image || '').trim(),
  bio: String(about.bio || '').trim(),
  approachParagraph: String(about.approachParagraph || '').trim(),
  credentials: Array.isArray(about.credentials)
    ? about.credentials.map((item) => String(item).trim()).filter(Boolean)
    : [],
  languages: Array.isArray(about.languages)
    ? about.languages.map((item) => String(item).trim()).filter(Boolean)
    : [],
  serviceAreaLine1: String(about.serviceAreaLine1 || '').trim(),
  serviceAreaLine2: String(about.serviceAreaLine2 || '').trim(),
  commitmentText: String(about.commitmentText || '').trim(),
  philosophyTitle: String(about.philosophyTitle || '').trim(),
  philosophyParagraph1: String(about.philosophyParagraph1 || '').trim(),
  philosophyParagraph2: String(about.philosophyParagraph2 || '').trim(),
});

const normalizeSpecialty = (item) => ({
  title: String(item.title || '').trim(),
  description: String(item.description || '').trim(),
});

const normalizeTestimonial = (item, fallbackId) => ({
  id: Number(item.id) || fallbackId,
  name: String(item.name || '').trim(),
  feedback: String(item.feedback || '').trim(),
  rating: Math.max(1, Math.min(5, Number(item.rating) || 5)),
});

const normalizeCoreValue = (item) => ({
  title: String(item.title || '').trim(),
  description: String(item.description || '').trim(),
});

const normalizeFaq = (item) => ({
  question: String(item.question || '').trim(),
  answer: String(item.answer || '').trim(),
});

const normalizeResource = (item) => ({
  title: String(item.title || '').trim(),
  description: String(item.description || '').trim(),
  url: String(item.url || '').trim(),
});

const normalizeLocalizedText = (value, fallback = createEmptyLocalizedText()) => {
  const source =
    value && typeof value === 'object' && !Array.isArray(value)
      ? value
      : { en: value };

  return SUPPORTED_LANGUAGES.reduce((accumulator, language) => {
    const nextValue = source?.[language.code];
    accumulator[language.code] = String(nextValue ?? fallback?.[language.code] ?? '').trim();
    return accumulator;
  }, {});
};

const normalizeServicesPage = (servicesPage = {}) => ({
  heroTitle: normalizeLocalizedText(servicesPage.heroTitle, DEFAULT_SERVICES_PAGE.heroTitle),
  heroSubtitle: normalizeLocalizedText(servicesPage.heroSubtitle, DEFAULT_SERVICES_PAGE.heroSubtitle),
  comparisonTitle: normalizeLocalizedText(servicesPage.comparisonTitle, DEFAULT_SERVICES_PAGE.comparisonTitle),
  faqTitle: normalizeLocalizedText(servicesPage.faqTitle, DEFAULT_SERVICES_PAGE.faqTitle),
  ctaTitle: normalizeLocalizedText(servicesPage.ctaTitle, DEFAULT_SERVICES_PAGE.ctaTitle),
  ctaSubtitle: normalizeLocalizedText(servicesPage.ctaSubtitle, DEFAULT_SERVICES_PAGE.ctaSubtitle),
  ctaButton: normalizeLocalizedText(servicesPage.ctaButton, DEFAULT_SERVICES_PAGE.ctaButton),
});

const LocalizedFieldEditor = ({ label, value, onChange, multiline = false }) => (
  <div className="space-y-3">
    <Label className="text-sage-700">{label}</Label>
    <div>
      <div>
          <Label className="text-xs uppercase tracking-wide text-sage-500">English</Label>
          {multiline ? (
            <Textarea
              value={value?.en || ''}
              onChange={(event) => onChange('en', event.target.value)}
              className="mt-1.5 border-sage-300 min-h-24"
            />
          ) : (
            <Input
              value={value?.en || ''}
              onChange={(event) => onChange('en', event.target.value)}
              className="mt-1.5 border-sage-300"
            />
          )}
        </div>
    </div>
  </div>
);

const AdminContent = () => {
  const [content, setContent] = useState(createDefaultContent);
  const [paymentSettings, setPaymentSettings] = useState(DEFAULT_PAYMENT_SETTINGS);
  const [siteSettingsPayload, setSiteSettingsPayload] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAboutImage, setIsUploadingAboutImage] = useState(false);
  const [status, setStatus] = useState({ type: '', text: '' });

  const [adminPassword, setAdminPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'about');

  const nextTestimonialId = useMemo(() => {
    const maxId = content.testimonials.reduce((max, item) => {
      const id = Number(item.id) || 0;
      return id > max ? id : max;
    }, 0);

    return maxId + 1;
  }, [content.testimonials]);

  const contentTabs = useMemo(
    () => [
      { key: 'about', label: 'About Section' },
      ...PAGE_EDITORS.map(({ key, label }) => ({ key, label })),
      { key: 'servicesPage', label: 'Services Page' },
      { key: 'specialties', label: 'Areas of Expertise' },
      { key: 'testimonials', label: 'Client Experiences' },
      { key: 'coreValues', label: 'Core Values' },
      { key: 'faqs', label: 'FAQs' },
      { key: 'resources', label: 'Helpful Resources' },
    ],
    [],
  );

  const verifyPassword = useCallback(async (password, silent = false) => {
    if (!password) {
      if (!silent) {
        setStatus({ type: 'error', text: 'Enter your admin password.' });
      }
      setIsCheckingAuth(false);
      setIsAuthenticated(false);
      return false;
    }

    setIsVerifying(true);

    try {
      const response = await fetch('/api/admin/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        throw new Error(`Auth failed (${response.status})`);
      }

      sessionStorage.setItem(STORAGE_KEY, password);
      setAdminPassword(password);
      setIsAuthenticated(true);
      setStatus({ type: '', text: '' });
      return true;
    } catch (error) {
      console.error(error);
      sessionStorage.removeItem(STORAGE_KEY);
      setIsAuthenticated(false);
      if (!silent) {
        setStatus({ type: 'error', text: 'Invalid admin password.' });
      }
      return false;
    } finally {
      setIsVerifying(false);
      setIsCheckingAuth(false);
    }
  }, []);

  const loadContent = useCallback(async () => {
    setIsLoading(true);

    try {
      const [sectionsResponse, siteSettingsResponse] = await Promise.all([
        fetch('/api/content/sections', {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        }),
        fetch('/api/content/site-settings', {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        }),
      ]);

      if (!sectionsResponse.ok) {
        throw new Error(`Failed to load sections content (${sectionsResponse.status})`);
      }

      if (!siteSettingsResponse.ok) {
        throw new Error(`Failed to load payment settings (${siteSettingsResponse.status})`);
      }

      const [sectionsPayload, settingsPayload] = await Promise.all([
        sectionsResponse.json(),
        siteSettingsResponse.json(),
      ]);

      setContent({
        about: normalizeAbout(sectionsPayload?.about),
        servicesPage: normalizeServicesPage(sectionsPayload?.servicesPage),
        publicPageCopy: sectionsPayload?.publicPageCopy || {},
        specialties: Array.isArray(sectionsPayload?.specialties)
          ? sectionsPayload.specialties.map(normalizeSpecialty)
          : [],
        testimonials: Array.isArray(sectionsPayload?.testimonials)
          ? sectionsPayload.testimonials.map((item, index) => normalizeTestimonial(item, index + 1))
          : [],
        coreValues: Array.isArray(sectionsPayload?.coreValues)
          ? sectionsPayload.coreValues.map(normalizeCoreValue)
          : [],
        faqs: Array.isArray(sectionsPayload?.faqs)
          ? sectionsPayload.faqs.map(normalizeFaq)
          : [],
        resources: Array.isArray(sectionsPayload?.resources)
          ? sectionsPayload.resources.map(normalizeResource)
          : [],
      });
      setSiteSettingsPayload(settingsPayload);
      setPaymentSettings(normalizePaymentSettings(settingsPayload));
      setStatus({ type: '', text: '' });
    } catch (error) {
      console.error(error);
      setStatus({
        type: 'error',
        text: 'Failed to load content. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const storedPassword = sessionStorage.getItem(STORAGE_KEY);

    if (!storedPassword) {
      setIsCheckingAuth(false);
      return;
    }

    setAdminPassword(storedPassword);
    verifyPassword(storedPassword, true);
  }, [verifyPassword]);

  useEffect(() => {
    if (isAuthenticated) {
      loadContent();
    }
  }, [isAuthenticated, loadContent]);

  const updateAboutField = (field, value) => {
    setContent((current) => ({
      ...current,
      about: {
        ...current.about,
        [field]: value,
      },
    }));
  };

  const updateServicesPageField = (field, language, value) => {
    setContent((current) => ({
      ...current,
      servicesPage: {
        ...current.servicesPage,
        [field]: {
          ...(current.servicesPage?.[field] || createEmptyLocalizedText()),
          [language]: value,
        },
      },
    }));
  };

  const updatePublicPageCopyField = (field, value) => {
    setContent((current) => ({
      ...current,
      publicPageCopy: { ...current.publicPageCopy, [field]: value },
    }));
  };

  const updateArrayItem = (section, index, field, value) => {
    setContent((current) => ({
      ...current,
      [section]: current[section].map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const updatePaymentField = (field, value) => {
    setPaymentSettings((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const addItem = (section) => {
    setContent((current) => {
      if (section === 'specialties') {
        return { ...current, specialties: [...current.specialties, createEmptySpecialty()] };
      }
      if (section === 'testimonials') {
        return {
          ...current,
          testimonials: [...current.testimonials, createEmptyTestimonial(nextTestimonialId)],
        };
      }
      if (section === 'coreValues') {
        return { ...current, coreValues: [...current.coreValues, createEmptyCoreValue()] };
      }
      if (section === 'faqs') {
        return { ...current, faqs: [...current.faqs, createEmptyFaq()] };
      }
      if (section === 'resources') {
        return { ...current, resources: [...current.resources, createEmptyResource()] };
      }
      return current;
    });
  };

  const removeItem = (section, index) => {
    setContent((current) => ({
      ...current,
      [section]: current[section].filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const uploadAdminImage = useCallback(
    async (file) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/upload-image', {
        method: 'POST',
        headers: {
          'X-Admin-Password': adminPassword,
        },
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 401) {
          sessionStorage.removeItem(STORAGE_KEY);
          setIsAuthenticated(false);
          throw new Error('Admin session expired. Please sign in again.');
        }

        const errorPayload = await response.json().catch(() => null);
        throw new Error(errorPayload?.detail || `Image upload failed (${response.status})`);
      }

      return response.json();
    },
    [adminPassword],
  );

  const handleAboutImageUpload = async (event) => {
    const imageFile = event.target.files?.[0];
    if (!imageFile) {
      return;
    }

    setIsUploadingAboutImage(true);
    try {
      const payload = await uploadAdminImage(imageFile);
      updateAboutField('image', payload.url || '');
      setStatus({ type: 'success', text: 'Hero image uploaded successfully.' });
    } catch (error) {
      console.error(error);
      setStatus({
        type: 'error',
        text: error?.message || 'Failed to upload image.',
      });
    } finally {
      setIsUploadingAboutImage(false);
      event.target.value = '';
    }
  };

  const handlePageCopyImageUpload = async (field, event) => {
    const imageFile = event.target.files?.[0];
    if (!imageFile) return;

    setIsUploadingAboutImage(true);
    try {
      const payload = await uploadAdminImage(imageFile);
      updatePublicPageCopyField(field, payload.url || '');
      setStatus({ type: 'success', text: 'Image uploaded successfully.' });
    } catch (error) {
      console.error(error);
      setStatus({ type: 'error', text: error?.message || 'Failed to upload image.' });
    } finally {
      setIsUploadingAboutImage(false);
      event.target.value = '';
    }
  };

  const saveContent = async () => {
    if (activeTab === 'payments') {
      if (!siteSettingsPayload || typeof siteSettingsPayload !== 'object') {
        setStatus({
          type: 'error',
          text: 'Payment settings are not loaded yet. Click Reload and try again.',
        });
        return;
      }

      const normalizedPayment = normalizePaymentSettings(paymentSettings);
      if (!normalizedPayment.enableManualPayments && !normalizedPayment.enablePaypalPayments) {
        setStatus({
          type: 'error',
          text: 'Enable at least one payment method (manual or PayPal).',
        });
        return;
      }

      if (normalizedPayment.enableManualPayments && !normalizedPayment.manualPaymentInstructions) {
        setStatus({
          type: 'error',
          text: 'Manual payment instructions are required when manual payments are enabled.',
        });
        return;
      }

      if (normalizedPayment.enablePaypalPayments && !normalizedPayment.paypalClientId) {
        setStatus({
          type: 'error',
          text: 'PayPal Client ID is required when PayPal payments are enabled.',
        });
        return;
      }

      if (!normalizedPayment.cancellationTerms) {
        setStatus({
          type: 'error',
          text: 'Cancellation terms are required.',
        });
        return;
      }

      setIsSaving(true);
      setStatus({ type: '', text: '' });

      try {
        const response = await fetch('/api/content/site-settings', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Admin-Password': adminPassword,
          },
          body: JSON.stringify({
            ...siteSettingsPayload,
            ...normalizedPayment,
          }),
        });

        if (!response.ok) {
          if (response.status === 401) {
            sessionStorage.removeItem(STORAGE_KEY);
            setIsAuthenticated(false);
            setStatus({ type: 'error', text: 'Admin session expired. Please sign in again.' });
            return;
          }

          throw new Error(`Failed to save payment settings (${response.status})`);
        }

        const payload = await response.json();
        setSiteSettingsPayload(payload);
        setPaymentSettings(normalizePaymentSettings(payload));
        setStatus({ type: 'success', text: 'Payment settings saved successfully.' });
      } catch (error) {
        console.error(error);
        setStatus({
          type: 'error',
          text: 'Failed to save payment settings. Please try again.',
        });
      } finally {
        setIsSaving(false);
      }
      return;
    }

    const normalizedPayload = {
      about: normalizeAbout(content.about),
      servicesPage: normalizeServicesPage(content.servicesPage),
      publicPageCopy: content.publicPageCopy || {},
      specialties: content.specialties.map(normalizeSpecialty),
      testimonials: content.testimonials.map((item, index) => normalizeTestimonial(item, index + 1)),
      coreValues: content.coreValues.map(normalizeCoreValue),
      faqs: content.faqs.map(normalizeFaq),
      resources: content.resources.map(normalizeResource),
    };

    if (!normalizedPayload.about.title || !normalizedPayload.about.subtitle || !normalizedPayload.about.bio) {
      setStatus({ type: 'error', text: 'About title, subtitle, and bio are required.' });
      return;
    }

    setIsSaving(true);
    setStatus({ type: '', text: '' });

    try {
      const response = await fetch('/api/content/sections', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Admin-Password': adminPassword,
        },
        body: JSON.stringify(normalizedPayload),
      });

      if (!response.ok) {
        if (response.status === 401) {
          sessionStorage.removeItem(STORAGE_KEY);
          setIsAuthenticated(false);
          setStatus({ type: 'error', text: 'Admin session expired. Please sign in again.' });
          return;
        }

        throw new Error(`Failed to save content (${response.status})`);
      }

      const payload = await response.json();
      setContent({
        about: normalizeAbout(payload?.about),
        servicesPage: normalizeServicesPage(payload?.servicesPage),
        publicPageCopy: payload?.publicPageCopy || {},
        specialties: Array.isArray(payload?.specialties)
          ? payload.specialties.map(normalizeSpecialty)
          : [],
        testimonials: Array.isArray(payload?.testimonials)
          ? payload.testimonials.map((item, index) => normalizeTestimonial(item, index + 1))
          : [],
        coreValues: Array.isArray(payload?.coreValues)
          ? payload.coreValues.map(normalizeCoreValue)
          : [],
        faqs: Array.isArray(payload?.faqs)
          ? payload.faqs.map(normalizeFaq)
          : [],
        resources: Array.isArray(payload?.resources)
          ? payload.resources.map(normalizeResource)
          : [],
      });

      setStatus({ type: 'success', text: 'Saved successfully.' });
    } catch (error) {
      console.error(error);
      setStatus({ type: 'error', text: 'Failed to save content. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSignIn = async (event) => {
    event.preventDefault();
    await verifyPassword(adminPassword, false);
  };

  const handleSignOut = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setIsAuthenticated(false);
    setAdminPassword("");
    setContent(createDefaultContent());
    setPaymentSettings(DEFAULT_PAYMENT_SETTINGS);
    setSiteSettingsPayload(null);
    setStatus({ type: '', text: '' });
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-sage-50 py-12">
        <div className="container mx-auto px-4 max-w-lg">
          <Card className="border-sage-200">
            <CardContent className="p-6 text-sage-700">Checking admin session...</CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-sage-50 py-12">
        <div className="container mx-auto px-4 max-w-lg">
          <Card className="border-sage-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sage-900">
                <Lock className="w-5 h-5" />
                Admin Access
              </CardTitle>
              <CardDescription>
                Enter the admin password to edit website content.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSignIn} className="space-y-4">
                <div>
                  <Label className="text-sage-700">Password</Label>
                  <Input
                    type="password"
                    value={adminPassword}
                    onChange={(event) => setAdminPassword(event.target.value)}
                    className="mt-1.5 border-sage-300"
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-sage-600 hover:bg-sage-700"
                  disabled={isVerifying}
                >
                  {isVerifying ? 'Verifying...' : 'Sign In'}
                </Button>
                {status.text && (
                  <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                    {status.text}
                  </p>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sage-50 py-12">
      <div className="container mx-auto px-4 max-w-6xl space-y-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-sage-900">Website Content Admin</h1>
            <p className="text-sage-600 mt-1">
              Edit the about page, services page copy, client experiences, expertise, values, FAQs, and resources.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Link to="/admin">
              <Button type="button" variant="outline" className="border-sage-300">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Admin Home
              </Button>
            </Link>
            <Button
              type="button"
              variant="outline"
              onClick={handleSignOut}
              className="border-sage-300"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={loadContent}
              disabled={isLoading || isSaving}
              className="border-sage-300"
            >
              <RefreshCcw className="w-4 h-4 mr-2" />
              Reload
            </Button>
            <Button
              type="button"
              onClick={saveContent}
              disabled={isLoading || isSaving}
              className="bg-sage-600 hover:bg-sage-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {status.text && (
          <div
            className={`rounded-lg px-4 py-3 text-sm font-medium ${
              status.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            {status.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-6">
          <Card className="border-sage-200 h-fit">
            <CardHeader>
              <CardTitle className="text-sage-900 text-lg">Content Tabs</CardTitle>
              <CardDescription>Select an editor section.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {contentTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`w-full text-left px-3 py-2 rounded-md border transition-colors ${
                    activeTab === tab.key
                      ? 'bg-sage-100 border-sage-300 text-sage-900'
                      : 'bg-white border-sage-200 text-sage-700 hover:bg-sage-50'
                  }`}
                >
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </CardContent>
          </Card>

          <div className="space-y-6">
            {activeTab === 'about' && (
              <Card className="border-sage-200 shadow-sm">
                <CardHeader className="bg-white border-b border-sage-200">
                  <CardTitle className="text-sage-900">About Section</CardTitle>
                  <CardDescription>
                    Controls the About page hero and main section content.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5 bg-white pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sage-700">Title</Label>
                <Input
                  value={content.about.title}
                  onChange={(event) => updateAboutField('title', event.target.value)}
                  className="mt-1.5 border-sage-300"
                />
              </div>
              <div>
                <Label className="text-sage-700">Subtitle</Label>
                <Input
                  value={content.about.subtitle}
                  onChange={(event) => updateAboutField('subtitle', event.target.value)}
                  className="mt-1.5 border-sage-300"
                />
              </div>
            </div>

            <div>
              <Label className="text-sage-700">Hero Image URL</Label>
              <Input
                value={content.about.image}
                onChange={(event) => updateAboutField('image', event.target.value)}
                className="mt-1.5 border-sage-300"
              />
              <div className="mt-3">
                <Label className="text-sage-700">Upload Image File</Label>
                <Input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleAboutImageUpload}
                  className="mt-1.5 border-sage-300"
                  disabled={isUploadingAboutImage || isSaving}
                />
                <p className="text-xs text-sage-500 mt-1">
                  {isUploadingAboutImage
                    ? 'Uploading image...'
                    : 'Allowed: JPG, PNG, WEBP, GIF. Max 5MB.'}
                </p>
              </div>
              {content.about.image && (
                <div className="mt-3 rounded-md border border-sage-200 overflow-hidden max-w-md">
                  <img src={content.about.image} alt="About hero preview" className="w-full h-44 object-cover" />
                </div>
              )}
            </div>

            <div>
              <Label className="text-sage-700">Bio</Label>
              <Textarea
                value={content.about.bio}
                onChange={(event) => updateAboutField('bio', event.target.value)}
                className="mt-1.5 border-sage-300 min-h-24"
              />
            </div>

            <div>
              <Label className="text-sage-700">Approach Paragraph</Label>
              <Textarea
                value={content.about.approachParagraph}
                onChange={(event) => updateAboutField('approachParagraph', event.target.value)}
                className="mt-1.5 border-sage-300 min-h-24"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sage-700">Languages (one per line)</Label>
                <Textarea
                  value={toLines(content.about.languages)}
                  onChange={(event) => updateAboutField('languages', parseLines(event.target.value))}
                  className="mt-1.5 border-sage-300 min-h-24"
                />
              </div>
              <div>
                <Label className="text-sage-700">Credentials (one per line)</Label>
                <Textarea
                  value={toLines(content.about.credentials)}
                  onChange={(event) => updateAboutField('credentials', parseLines(event.target.value))}
                  className="mt-1.5 border-sage-300 min-h-24"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sage-700">Service Area Line 1</Label>
                <Input
                  value={content.about.serviceAreaLine1}
                  onChange={(event) => updateAboutField('serviceAreaLine1', event.target.value)}
                  className="mt-1.5 border-sage-300"
                />
              </div>
              <div>
                <Label className="text-sage-700">Service Area Line 2</Label>
                <Input
                  value={content.about.serviceAreaLine2}
                  onChange={(event) => updateAboutField('serviceAreaLine2', event.target.value)}
                  className="mt-1.5 border-sage-300"
                />
              </div>
            </div>

            <div>
              <Label className="text-sage-700">Commitment Text</Label>
              <Textarea
                value={content.about.commitmentText}
                onChange={(event) => updateAboutField('commitmentText', event.target.value)}
                className="mt-1.5 border-sage-300 min-h-24"
              />
            </div>

            <div>
              <Label className="text-sage-700">Philosophy Title</Label>
              <Input
                value={content.about.philosophyTitle}
                onChange={(event) => updateAboutField('philosophyTitle', event.target.value)}
                className="mt-1.5 border-sage-300"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sage-700">Philosophy Paragraph 1</Label>
                <Textarea
                  value={content.about.philosophyParagraph1}
                  onChange={(event) => updateAboutField('philosophyParagraph1', event.target.value)}
                  className="mt-1.5 border-sage-300 min-h-24"
                />
              </div>
              <div>
                <Label className="text-sage-700">Philosophy Paragraph 2</Label>
                <Textarea
                  value={content.about.philosophyParagraph2}
                  onChange={(event) => updateAboutField('philosophyParagraph2', event.target.value)}
                  className="mt-1.5 border-sage-300 min-h-24"
                />
              </div>
            </div>
                </CardContent>
              </Card>
            )}

            {activeTab === 'servicesPage' && (
              <Card className="border-sage-200 shadow-sm">
                <CardHeader className="bg-white border-b border-sage-200">
                  <CardTitle className="text-sage-900">Services Page</CardTitle>
                  <CardDescription>
                    Controls the editable headings and call-to-action copy on the services page.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5 bg-white pt-6">
                  <LocalizedFieldEditor
                    label="Hero Title"
                    value={content.servicesPage.heroTitle}
                    onChange={(language, value) => updateServicesPageField('heroTitle', language, value)}
                  />

                  <LocalizedFieldEditor
                    label="Hero Subtitle"
                    value={content.servicesPage.heroSubtitle}
                    onChange={(language, value) => updateServicesPageField('heroSubtitle', language, value)}
                    multiline
                  />

                  <LocalizedFieldEditor
                    label="Comparison Section Title"
                    value={content.servicesPage.comparisonTitle}
                    onChange={(language, value) => updateServicesPageField('comparisonTitle', language, value)}
                  />

                  <LocalizedFieldEditor
                    label="FAQ Section Title"
                    value={content.servicesPage.faqTitle}
                    onChange={(language, value) => updateServicesPageField('faqTitle', language, value)}
                  />

                  <LocalizedFieldEditor
                    label="CTA Title"
                    value={content.servicesPage.ctaTitle}
                    onChange={(language, value) => updateServicesPageField('ctaTitle', language, value)}
                  />

                  <LocalizedFieldEditor
                    label="CTA Subtitle"
                    value={content.servicesPage.ctaSubtitle}
                    onChange={(language, value) => updateServicesPageField('ctaSubtitle', language, value)}
                    multiline
                  />

                  <LocalizedFieldEditor
                    label="CTA Button Label"
                    value={content.servicesPage.ctaButton}
                    onChange={(language, value) => updateServicesPageField('ctaButton', language, value)}
                  />
                </CardContent>
              </Card>
            )}

            {PAGE_EDITORS.filter((editor) => editor.key === activeTab).map((editor) => (
              <Card key={editor.key} className="border-sage-200 shadow-sm">
                <CardHeader className="bg-white border-b border-sage-200">
                  <CardTitle className="text-sage-900">{editor.title}</CardTitle>
                  <CardDescription>{editor.description} Leave a field blank to use the built-in label.</CardDescription>
                </CardHeader>
                <CardContent className="bg-white pt-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {editor.fields.map(([key, label, type]) => (
                          <div key={key} className={type === 'textarea' ? 'md:col-span-2' : ''}>
                            <Label className="text-sage-700">{label}</Label>
                            {type === 'textarea' ? (
                              <Textarea value={content.publicPageCopy?.[key] || ''} onChange={(event) => updatePublicPageCopyField(key, event.target.value)} className="mt-1.5 border-sage-300 min-h-20" />
                            ) : (
                              <Input value={content.publicPageCopy?.[key] || ''} onChange={(event) => updatePublicPageCopyField(key, event.target.value)} className="mt-1.5 border-sage-300" />
                            )}
                            {type === 'image' && (
                              <div className="mt-3 space-y-3">
                                <Input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={(event) => handlePageCopyImageUpload(key, event)} disabled={isUploadingAboutImage || isSaving} className="border-sage-300" />
                                {content.publicPageCopy?.[key] && <img src={content.publicPageCopy[key]} alt="Home hero preview" className="h-32 w-full rounded-md border border-sage-200 object-cover" />}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                </CardContent>
              </Card>
            ))}

            {activeTab === 'specialties' && (
              <Card className="border-sage-200 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between bg-white border-b border-sage-200">
            <div>
              <CardTitle className="text-sage-900">Areas of Expertise</CardTitle>
              <CardDescription>Shown on home page expertise section.</CardDescription>
            </div>
            <Button type="button" variant="outline" onClick={() => addItem('specialties')} className="border-sage-300">
              <Plus className="w-4 h-4 mr-2" />
              Add
            </Button>
                </CardHeader>
                <CardContent className="space-y-4 bg-white pt-6">
            {content.specialties.map((item, index) => (
              <div key={`specialty-${index}`} className="rounded-lg border border-sage-200 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sage-900">Item #{index + 1}</h3>
                  <Button type="button" variant="outline" size="sm" className="border-red-200 text-red-700 hover:bg-red-50" onClick={() => removeItem('specialties', index)}>
                    <Trash2 className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                </div>
                <div>
                  <Label className="text-sage-700">Title</Label>
                  <Input value={item.title} onChange={(event) => updateArrayItem('specialties', index, 'title', event.target.value)} className="mt-1.5 border-sage-300" />
                </div>
                <div>
                  <Label className="text-sage-700">Description</Label>
                  <Textarea value={item.description} onChange={(event) => updateArrayItem('specialties', index, 'description', event.target.value)} className="mt-1.5 border-sage-300 min-h-20" />
                </div>
              </div>
            ))}
                </CardContent>
              </Card>
            )}

            {activeTab === 'testimonials' && (
              <Card className="border-sage-200 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between bg-white border-b border-sage-200">
            <div>
              <CardTitle className="text-sage-900">Client Experiences</CardTitle>
              <CardDescription>Testimonials shown on home page.</CardDescription>
            </div>
            <Button type="button" variant="outline" onClick={() => addItem('testimonials')} className="border-sage-300">
              <Plus className="w-4 h-4 mr-2" />
              Add
            </Button>
                </CardHeader>
                <CardContent className="space-y-4 bg-white pt-6">
            {content.testimonials.map((item, index) => (
              <div key={`testimonial-${index}`} className="rounded-lg border border-sage-200 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sage-900">Testimonial #{index + 1}</h3>
                  <Button type="button" variant="outline" size="sm" className="border-red-200 text-red-700 hover:bg-red-50" onClick={() => removeItem('testimonials', index)}>
                    <Trash2 className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-sage-700">ID</Label>
                    <Input type="number" value={item.id} onChange={(event) => updateArrayItem('testimonials', index, 'id', Number(event.target.value) || '')} className="mt-1.5 border-sage-300" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-sage-700">Client Name</Label>
                    <Input value={item.name} onChange={(event) => updateArrayItem('testimonials', index, 'name', event.target.value)} className="mt-1.5 border-sage-300" />
                  </div>
                </div>
                <div>
                  <Label className="text-sage-700">Feedback</Label>
                  <Textarea value={item.feedback} onChange={(event) => updateArrayItem('testimonials', index, 'feedback', event.target.value)} className="mt-1.5 border-sage-300 min-h-20" />
                </div>
                <div>
                  <Label className="text-sage-700">Rating (1-5)</Label>
                  <Input type="number" min="1" max="5" value={item.rating} onChange={(event) => updateArrayItem('testimonials', index, 'rating', Number(event.target.value) || 5)} className="mt-1.5 border-sage-300" />
                </div>
              </div>
            ))}
                </CardContent>
              </Card>
            )}

            {activeTab === 'coreValues' && (
              <Card className="border-sage-200 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between bg-white border-b border-sage-200">
            <div>
              <CardTitle className="text-sage-900">Core Values</CardTitle>
              <CardDescription>Values displayed on the About page.</CardDescription>
            </div>
            <Button type="button" variant="outline" onClick={() => addItem('coreValues')} className="border-sage-300">
              <Plus className="w-4 h-4 mr-2" />
              Add
            </Button>
                </CardHeader>
                <CardContent className="space-y-4 bg-white pt-6">
            {content.coreValues.map((item, index) => (
              <div key={`core-value-${index}`} className="rounded-lg border border-sage-200 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sage-900">Value #{index + 1}</h3>
                  <Button type="button" variant="outline" size="sm" className="border-red-200 text-red-700 hover:bg-red-50" onClick={() => removeItem('coreValues', index)}>
                    <Trash2 className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                </div>
                <div>
                  <Label className="text-sage-700">Title</Label>
                  <Input value={item.title} onChange={(event) => updateArrayItem('coreValues', index, 'title', event.target.value)} className="mt-1.5 border-sage-300" />
                </div>
                <div>
                  <Label className="text-sage-700">Description</Label>
                  <Textarea value={item.description} onChange={(event) => updateArrayItem('coreValues', index, 'description', event.target.value)} className="mt-1.5 border-sage-300 min-h-20" />
                </div>
              </div>
            ))}
                </CardContent>
              </Card>
            )}

            {activeTab === 'faqs' && (
              <Card className="border-sage-200 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between bg-white border-b border-sage-200">
            <div>
              <CardTitle className="text-sage-900">FAQs</CardTitle>
              <CardDescription>Frequently asked questions shown on services page.</CardDescription>
            </div>
            <Button type="button" variant="outline" onClick={() => addItem('faqs')} className="border-sage-300">
              <Plus className="w-4 h-4 mr-2" />
              Add
            </Button>
                </CardHeader>
                <CardContent className="space-y-4 bg-white pt-6">
            {content.faqs.map((item, index) => (
              <div key={`faq-${index}`} className="rounded-lg border border-sage-200 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sage-900">FAQ #{index + 1}</h3>
                  <Button type="button" variant="outline" size="sm" className="border-red-200 text-red-700 hover:bg-red-50" onClick={() => removeItem('faqs', index)}>
                    <Trash2 className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                </div>
                <div>
                  <Label className="text-sage-700">Question</Label>
                  <Input value={item.question} onChange={(event) => updateArrayItem('faqs', index, 'question', event.target.value)} className="mt-1.5 border-sage-300" />
                </div>
                <div>
                  <Label className="text-sage-700">Answer</Label>
                  <Textarea value={item.answer} onChange={(event) => updateArrayItem('faqs', index, 'answer', event.target.value)} className="mt-1.5 border-sage-300 min-h-20" />
                </div>
              </div>
            ))}
                </CardContent>
              </Card>
            )}

            {activeTab === 'resources' && (
              <Card className="border-sage-200 shadow-sm">
                <CardHeader className="flex flex-row items-center justify-between bg-white border-b border-sage-200">
            <div>
              <CardTitle className="text-sage-900">Helpful Resources</CardTitle>
              <CardDescription>Resource links shown on contact page.</CardDescription>
            </div>
            <Button type="button" variant="outline" onClick={() => addItem('resources')} className="border-sage-300">
              <Plus className="w-4 h-4 mr-2" />
              Add
            </Button>
                </CardHeader>
                <CardContent className="space-y-4 bg-white pt-6">
            {content.resources.map((item, index) => (
              <div key={`resource-${index}`} className="rounded-lg border border-sage-200 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sage-900">Resource #{index + 1}</h3>
                  <Button type="button" variant="outline" size="sm" className="border-red-200 text-red-700 hover:bg-red-50" onClick={() => removeItem('resources', index)}>
                    <Trash2 className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                </div>
                <div>
                  <Label className="text-sage-700">Title</Label>
                  <Input value={item.title} onChange={(event) => updateArrayItem('resources', index, 'title', event.target.value)} className="mt-1.5 border-sage-300" />
                </div>
                <div>
                  <Label className="text-sage-700">Description</Label>
                  <Textarea value={item.description} onChange={(event) => updateArrayItem('resources', index, 'description', event.target.value)} className="mt-1.5 border-sage-300 min-h-20" />
                </div>
                <div>
                  <Label className="text-sage-700">URL</Label>
                  <Input value={item.url} onChange={(event) => updateArrayItem('resources', index, 'url', event.target.value)} className="mt-1.5 border-sage-300" />
                </div>
              </div>
            ))}
                </CardContent>
              </Card>
            )}

            {activeTab === 'payments' && (
              <Card className="border-sage-200 shadow-sm">
                <CardHeader className="bg-white border-b border-sage-200">
                  <CardTitle className="text-sage-900">Payments</CardTitle>
                  <CardDescription>
                    Configure manual and PayPal checkout options, tax, and online booking fee.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5 bg-white pt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="flex items-center gap-2 rounded-md border border-sage-200 px-3 py-2">
                      <input
                        type="checkbox"
                        checked={Boolean(paymentSettings.enableManualPayments)}
                        onChange={(event) =>
                          updatePaymentField('enableManualPayments', event.target.checked)
                        }
                      />
                      <span className="text-sage-800 text-sm">Enable manual payments</span>
                    </label>

                    <label className="flex items-center gap-2 rounded-md border border-sage-200 px-3 py-2">
                      <input
                        type="checkbox"
                        checked={Boolean(paymentSettings.enablePaypalPayments)}
                        onChange={(event) =>
                          updatePaymentField('enablePaypalPayments', event.target.checked)
                        }
                      />
                      <span className="text-sage-800 text-sm">Enable PayPal payments</span>
                    </label>
                  </div>

                  <div>
                    <Label className="text-sage-700">Manual Payment Instructions</Label>
                    <Textarea
                      value={paymentSettings.manualPaymentInstructions}
                      onChange={(event) =>
                        updatePaymentField('manualPaymentInstructions', event.target.value)
                      }
                      className="mt-1.5 border-sage-300 min-h-24"
                    />
                    <p className="text-xs text-sage-500 mt-1">
                      Shown to users when manual payment is selected.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <Label className="text-sage-700">PayPal Client ID</Label>
                      <Input
                        value={paymentSettings.paypalClientId}
                        onChange={(event) => updatePaymentField('paypalClientId', event.target.value)}
                        className="mt-1.5 border-sage-300"
                      />
                    </div>
                    <div>
                      <Label className="text-sage-700">PayPal Environment</Label>
                      <select
                        value={paymentSettings.paypalEnvironment}
                        onChange={(event) =>
                          updatePaymentField('paypalEnvironment', event.target.value)
                        }
                        className="mt-1.5 w-full rounded-md border border-sage-300 bg-white px-3 py-2 text-sm"
                      >
                        <option value="sandbox">sandbox</option>
                        <option value="live">live</option>
                      </select>
                    </div>
                  </div>

                  <div className="max-w-xs">
                    <Label className="text-sage-700">PayPal Currency</Label>
                    <Input
                      value={paymentSettings.paypalCurrency}
                      onChange={(event) => updatePaymentField('paypalCurrency', event.target.value)}
                      className="mt-1.5 border-sage-300"
                      placeholder="EUR"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sage-700">Online Payment Tax (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={paymentSettings.onlinePaymentTaxPercent}
                        onChange={(event) =>
                          updatePaymentField('onlinePaymentTaxPercent', event.target.value)
                        }
                        className="mt-1.5 border-sage-300"
                      />
                    </div>
                    <div>
                      <Label className="text-sage-700">Online Booking Fee (flat)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={paymentSettings.onlinePaymentBookingFee}
                        onChange={(event) =>
                          updatePaymentField('onlinePaymentBookingFee', event.target.value)
                        }
                        className="mt-1.5 border-sage-300"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sage-700">Minimum Booking Payment (%)</Label>
                      <Input
                        type="number"
                        min="50"
                        max="100"
                        step="0.01"
                        value={paymentSettings.onlinePaymentMinimumPercent}
                        onChange={(event) =>
                          updatePaymentField('onlinePaymentMinimumPercent', event.target.value)
                        }
                        className="mt-1.5 border-sage-300"
                      />
                      <p className="text-xs text-sage-500 mt-1">
                        Customers must pay at least this percentage now, or pay full.
                      </p>
                    </div>
                    <div>
                      <Label className="text-sage-700">Cancellation Window (hours)</Label>
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        value={paymentSettings.cancellationWindowHours}
                        onChange={(event) =>
                          updatePaymentField('cancellationWindowHours', event.target.value)
                        }
                        className="mt-1.5 border-sage-300"
                      />
                    </div>
                  </div>

                  <div className="max-w-sm">
                    <Label className="text-sage-700">Post-Booking Buffer (minutes)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={paymentSettings.bookingBufferMinutes}
                      onChange={(event) =>
                        updatePaymentField('bookingBufferMinutes', event.target.value)
                      }
                      className="mt-1.5 border-sage-300"
                    />
                    <p className="text-xs text-sage-500 mt-1">
                      Added after each confirmed/completed booking to prevent overlap.
                    </p>
                  </div>

                  <div>
                    <Label className="text-sage-700">Cancellation Terms</Label>
                    <Textarea
                      value={paymentSettings.cancellationTerms}
                      onChange={(event) =>
                        updatePaymentField('cancellationTerms', event.target.value)
                      }
                      className="mt-1.5 border-sage-300 min-h-24"
                    />
                    <p className="text-xs text-sage-500 mt-1">
                      Shown on booking checkout and invoice PDF.
                    </p>
                  </div>

                  <p className="text-xs text-sage-500">
                    Security: set backend env var <code>PAYPAL_CLIENT_SECRET</code> in <code>backend/.env</code>.
                    It is not exposed in public site settings.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminContent;
